import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	generatePath,
	useHistory,
	useLocation,
	useParams,
} from "react-router-dom";
import routing from "routing";
import actions from "src/actions/Exam";
import Alert from "src/components/Default/Alert";
import QuestionBox from "src/components/Exam/QuestionBox/QuestionBox";
import QuestionDrawer from "src/components/Exam/QuestionsDrawer";
import { GlobalChatbot } from "src/components/GlobalChatbot/GlobalChatbot";
import { isMultiAnswersExam } from "src/helpers/examHelpers";
import { isTablet } from "src/helpers/isMobile";
import { isAiChatbotEnabled } from "src/hooks/useAppConfig";
import {
	getExam,
	setActiveQuestion,
	usePatchExam,
	useSetAnswer,
} from "src/hooks/useExam";
import { useQuestionSettings } from "src/reducers/exam";
import { ExamState, ExamStatus } from "src/types/enums";

interface ExamProps {
	viewOnly?: boolean;
	examName?: string;
	history: { push: (location: string | object) => void };
}

interface AnswerPayload {
	questionId: string | number;
	answer: string;
	isCorrectAnswer: boolean;
	isAnnulledQuestion: boolean;
}

interface ExamQuestion {
	id: string | number;
	pytanie: string;
	answer: string | null;
	correctAnswer: string;
	isCorrectAnswer: boolean;
	isCanceled: boolean;
	answers: Record<string, string>;
	images?: Array<{ url: string; [key: string]: unknown }>;
	excludedAnswers: Array<{ answer: string }>;
}

function Exam(props: ExamProps) {
	const params = useParams<{ id: string; questionId: string }>();
	const examId = params.id;
	const {
		question: selectedQuestion,
		questions,
		type,
		status,
		state: examState,
	} = getExam(examId);
	const { t } = useTranslation();
	// w trakcie symulacji chowamy pomoce (jak omówienia) — chatbot wróci po zakończeniu
	const isSolvingSimulation =
		isMultiAnswersExam(type) && status === ExamStatus.ACTIVE;
	const { mutateAsync: setAnswer } = useSetAnswer() as unknown as {
		mutateAsync: (payload: AnswerPayload & { examId: string }) => Promise<void>;
	};
	const { mutateAsync: patchExam } = usePatchExam() as unknown as {
		mutateAsync: (payload: { id: string; state: string }) => Promise<void>;
	};
	const location = useLocation();
	const [errors, setErrors] = useState<string | null>(null);
	const [isMenuOpen, setIsMenuOpen] = useState(!isTablet());
	const questionId = +params.questionId - 1;
	const { dispatch } = useQuestionSettings();
	const history = useHistory();

	// biome-ignore lint/correctness/useExhaustiveDependencies: aktywne pytanie synchronizujemy tylko przy zmianie numeru pytania w URL
	useEffect(() => {
		setActiveQuestion(examId, questionId);
	}, [questionId]);

	const isAnswerSet = (): boolean => {
		return !!selectedQuestion.answer;
	};

	const getAnswerForQuestion = (): string | null => {
		return selectedQuestion.answer;
	};

	const setCorrectAnswer = async (answer: AnswerPayload) => {
		if (
			props.viewOnly ||
			(!!selectedQuestion.answer && !isMultiAnswersExam(type))
		) {
			return null;
		}

		if (examState === ExamState.PAUSED) {
			await patchExam({
				id: examId,
				state: ExamState.PENDING,
			});
		}

		try {
			await setAnswer({
				...answer,
				examId,
			});
		} catch (err: unknown) {
			const error = err as { response?: { status: number } };
			if (error.response && error.response.status === 401) {
				return props.history.push({
					pathname: routing.paths.signIn,
					search: `?${new URLSearchParams({
						redirectUrl: location.pathname + location.search,
					}).toString()}`,
					state: {
						message: t("errors.sessionHasExpired"),
					},
				});
			}

			setErrors(t("errors.somethingWentWrong"));
		}
	};

	const getCurrentIndex = (): number => {
		return questions.findIndex(
			(question: ExamQuestion) => question.id === selectedQuestion.id,
		);
	};

	const getElementCurrentIndex = (element: ExamQuestion): number => {
		return questions.findIndex(
			(question: ExamQuestion) => question.id === element.id,
		);
	};

	const onNext = () => {
		const index = getCurrentIndex();
		if (questions[index + 1]) {
			setSelectedQuestion(questions[index + 1]);
		}
	};

	const onPrev = () => {
		const index = getCurrentIndex();
		if (questions[index - 1]) {
			setSelectedQuestion(questions[index - 1]);
		}
	};

	const resetSettings = () => {
		dispatch(actions.resetSettings());
	};

	const setSelectedQuestion = (element: ExamQuestion) => {
		const index = getElementCurrentIndex(element);
		// search zostaje — stan czatbota (chatbotOpen/chatbotId) siedzi w query
		history.push({
			pathname: generatePath(routing.paths.exam, {
				id: examId,
				questionId: index + 1,
			}),
			search: location.search,
		});
		resetSettings();
	};

	const renderError = () => {
		return (
			errors && (
				<Alert
					setValid={() => setErrors(null)}
					type="error"
					message={errors}
					isOpen
				/>
			)
		);
	};

	const toggleIsMenuOpen = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	const isLastQuestion = (): boolean => {
		const index = getCurrentIndex();
		return !questions[index + 1];
	};

	const isFirstQuestion = (): boolean => {
		const index = getCurrentIndex();
		return !questions[index - 1];
	};

	return (
		selectedQuestion &&
		questions && (
			<>
				{renderError()}
				<div className="exam">
					<QuestionDrawer
						selected={selectedQuestion}
						onSelect={setSelectedQuestion}
						items={questions}
						isOpen={isMenuOpen}
						toggleIsMenuOpen={toggleIsMenuOpen}
						lastAnsweredQuestionIndex={questionId}
						examId={examId}
						viewOnly={props.viewOnly}
						examName={props.examName}
					/>
					<QuestionBox
						examStatus={status}
						examType={type}
						selectedQuestion={selectedQuestion}
						setAnswer={setCorrectAnswer}
						isAnswerSet={isAnswerSet()}
						onNext={onNext}
						onPrev={onPrev}
						viewOnly={props.viewOnly}
						userAnswer={getAnswerForQuestion()}
						isAnnulledQuestion={selectedQuestion.isCanceled}
						isLastQuestion={isLastQuestion()}
						isFirstQuestion={isFirstQuestion()}
						toggleIsMenuOpen={toggleIsMenuOpen}
					/>
					{/* w flexie .exam — na desktopie chatbot dokuje się z prawej jak lista pytań */}
					{isAiChatbotEnabled() && !isSolvingSimulation && (
						<GlobalChatbot
							attachableQuestion={{
								id: Number(selectedQuestion.id),
								preview: selectedQuestion.pytanie.replace(/<[^>]*>/g, ""),
							}}
						/>
					)}
				</div>
			</>
		)
	);
}

export default Exam;
